import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, TrendingUp, Award, Copy } from "lucide-react";

import { referralApi } from "@/lib/extensions-api";
import { formatCurrency, formatNumber, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import { StatsCard } from "./components/StatsCard";
import { ReferralTree } from "./components/ReferralTree";
import { useAuthStore } from "@/store/auth-store";
import { toast } from "sonner";

export function ReferralPage() {
  const [tab, setTab] = useState<"tree" | "commissions">("tree");
  const user = useAuthStore((s) => s.user);

  const summary = useQuery({
    queryKey: ["referral-summary"],
    queryFn: referralApi.summary,
  });
  const tree = useQuery({
    queryKey: ["referral-tree"],
    queryFn: () => referralApi.tree(),
    enabled: tab === "tree",
  });
  const commissions = useQuery({
    queryKey: ["referral-commissions"],
    queryFn: referralApi.commissions,
    enabled: tab === "commissions",
  });

  const copyMyId = async () => {
    if (!user) return;
    await navigator.clipboard.writeText(user.user_id);
    toast.success("User ID copied — share it with new members!");
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Referral Network</h1>
        <p className="text-sm text-surface-500 mt-1">
          Every wallet credit earned by your direct referrals gives you a 5% commission automatically.
        </p>
      </header>

      {/* Sharing card */}
      <Card className="bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 text-white border-0">
        <CardBody className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-brand-100">Your referral ID</div>
            <div className="mt-1 text-3xl font-bold font-mono">{user?.user_id}</div>
            <div className="mt-1 text-sm text-brand-100">
              Share this ID — anyone joining via you becomes your direct referral.
            </div>
          </div>
          <button
            onClick={copyMyId}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-brand-700 font-semibold hover:bg-brand-50 transition-colors"
          >
            <Copy className="h-4 w-4" /> Copy ID
          </button>
        </CardBody>
      </Card>

      {/* Stats */}
      {summary.isLoading ? (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <StatsCard icon={Users} tone="info" label="Direct Referrals"
                      value={formatNumber(summary.data?.direct_referrals ?? 0)} />
          <StatsCard icon={TrendingUp} tone="warning" label="Total Descendants"
                      value={formatNumber(summary.data?.total_descendants ?? 0)}
                      hint="All generations" />
          <StatsCard icon={Award} tone="success" label="Total Commission Earned"
                      value={formatCurrency(summary.data?.total_commission ?? "0")} />
          <StatsCard icon={Users} tone="neutral" label="Your Sponsor"
                      value={summary.data?.sponsor_user_id ?? "—"} />
        </div>
      )}

      <Card>
        <div className="px-5 pt-4">
          <Tabs value={tab} onChange={(v) => setTab(v as "tree" | "commissions")} tabs={[
            { value: "tree",        label: "Referral Tree" },
            { value: "commissions", label: "Commission History",
              count: commissions.data?.length },
          ]} />
        </div>
        <CardBody className={tab === "tree" ? "" : "p-0"}>
          {tab === "tree" && (
            tree.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-lg" />
                ))}
              </div>
            ) : tree.data ? <ReferralTree tree={tree.data} />
                            : <EmptyState icon={Users} title="No referrals yet" />
          )}

          {tab === "commissions" && (
            commissions.isLoading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : (commissions.data ?? []).length === 0 ? (
              <EmptyState icon={Award} title="No commissions yet"
                            description="You'll earn 5% whenever a direct referral receives a wallet credit." />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Source User</TH>
                    <TH className="text-right">Source Amount</TH>
                    <TH className="text-right">Rate</TH>
                    <TH className="text-right">Your Commission</TH>
                    <TH>Status</TH>
                    <TH>When</TH>
                  </TR>
                </THead>
                <tbody>
                  {commissions.data!.map((c) => (
                    <TR key={c.id}>
                      <TD><CopyableId value={c.source_user_id} /></TD>
                      <TD className="text-right">{formatCurrency(c.source_amount)}</TD>
                      <TD className="text-right text-xs">
                        {(Number(c.commission_rate) * 100).toFixed(2)}%
                      </TD>
                      <TD className="text-right font-semibold text-success-700">
                        {formatCurrency(c.commission_amount)}
                      </TD>
                      <TD><StatusPill status={c.status} /></TD>
                      <TD className="text-xs text-surface-500">{formatDateTime(c.created_at)}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            )
          )}
        </CardBody>
      </Card>
    </div>
  );
}
